import loadable from '@loadable/component';
import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { BurgerMenu as BurgerMenuIcon, User, UserFilled } from '@ori-ui/icons';
import type { SvgIcon } from '@ori-ui/mui';
import { Backdrop, Modal, SHADOW_LEVEL1, Slide, styled, useTheme } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import type { GlobalCustomersCustomerInfoDto } from '@ori/global-api-client/esm/models';
import type { PersonalMenuProps } from '@ori/personal-menu';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { lazy, memo, useCallback, useState, useEffect } from 'react';

import { BurgerMenuContent } from './BurgerMenuContent';
import { BurgerMenuLinks } from './BurgerMenuLinks/BurgerMenuLinks';
import type { BurgerMenuLinksProps } from './BurgerMenuLinks/types';
import { BurgerMenuTab } from './BurgerMenuTab';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { useMobileWidth, useTranslationByKey } from '~/hooks';
import { BurgerMenuTabNames, TranslationKeys } from '~/models';

const BurgerMenuRoot = styled('aside')<{ ownerState: { isMobile?: boolean; isDesktop?: boolean } }>(
  ({ ownerState, theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[100]}`,
    display: 'none',
    transition: 'top var(--top-nav-height-animation)',

    ...(ownerState.isMobile && {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxWidth: 'calc(100% - 45px)',
      position: 'absolute',
      width: 330,
    }),

    ...(ownerState.isDesktop && {
      boxShadow: SHADOW_LEVEL1,
      display: 'flex',
      left: 0,
      minHeight: 590,
      position: 'absolute',
      top: 'var(--top-nav-height)',
      width: '100%',
    }),
  }),
);

const BurgerMenuContainer = styled('div')(() => ({
  display: 'flex',
  height: 54,
  width: '100%',
}));

const LazyPersonalMenu = loadable(async () =>
  import('@ori/personal-menu').then((mod) => mod.PersonalMenu),
) as VFC<PersonalMenuProps>;

const LazyAccount = lazy(async () => import('../Account/LoginLibrary')) as VFC;

const LazyBurgerMenuLinks = lazy(
  async () => import('./BurgerMenuLinks/BurgerMenuLinks'),
) as VFC<BurgerMenuLinksProps>;

interface BurgerMenuProps
  extends Pick<GlobalCustomersCustomerInfoDto, 'consultantNumber' | 'customerType'> {
  lang: string;
}

export const BurgerMenu: VFC<BurgerMenuProps> = memo(({ consultantNumber, customerType, lang }) => {
  const { BurgerMenu: BurgerMenuType, AccountSideMenu: AccountSideMenuType } =
    ToggleMenuFlyout.MenuTypes;
  const { activeMenu, burgerMenuTab, toggleMenu, setPbsContentIsShown } = useNavigationContext();
  const { pbsIconInBurgerMenu } = useAppConfigurationContext();
  const [isAccountTabActive, setIsAccountTabActive] = useState(false);
  const isMobile = useMobileWidth();
  const ownerState = { isMobile };
  const isBrandPartner = customerType?.customerTypeId === 1;
  const handleTabClick = useCallback(
    (tabName: BurgerMenuTabNames) => {
      const menuType: ToggleMenuFlyout.MenuTypes =
        tabName === BurgerMenuTabNames.ProductsTab ? BurgerMenuType : AccountSideMenuType;
      toggleMenu(menuType);
      setIsAccountTabActive(tabName === BurgerMenuTabNames.AccountTab);
    },
    [AccountSideMenuType, BurgerMenuType, toggleMenu],
  );
  const translations = useTranslationByKey();
  const {
    globalApiUrl,
    staticApiUrl,
    tenant,
    personalMenuInitialData,
    mobileOfficeApiUrl,
    consultantNumber: configConsultantNumber,
    consultantType,
    reportsItemName,
  } = useAppConfigurationContext();

  const { zIndex } = useTheme();

  const { customerId, isAnonymousUser, token } = useToken();

  const isLoggedIn = !isAnonymousUser;
  const isMenuActive =
    activeMenu === BurgerMenuType || (isMobile && activeMenu === AccountSideMenuType);
  const activeTab =
    activeMenu === BurgerMenuType ? BurgerMenuTabNames.ProductsTab : BurgerMenuTabNames.AccountTab;
  const { getTestId } = useTestId();

  // Show PBS content as default view if PBS is active
  useEffect(() => {
    if (pbsIconInBurgerMenu) {
      setPbsContentIsShown(true);
    }

    return () => setPbsContentIsShown(false);
  }, [pbsIconInBurgerMenu, setPbsContentIsShown]);

  const accountTabContent = isAnonymousUser ? (
    <LazyAccount />
  ) : (
    <LazyPersonalMenu
      configuration={{
        consultantNumber: consultantNumber?.toString() ?? configConsultantNumber,
        customerId,
        globalApiUrl,
        lang,
        personalMenuInitialData,
        staticApiUrl,
        tenant,
        token,
        mobileOfficeApiUrl,
        consultantType,
        reportsItemName,
      }}
    />
  );

  const onClickOverlay = useCallback(() => {
    setIsAccountTabActive(false);
    toggleMenu(BurgerMenuType);
  }, [BurgerMenuType, setIsAccountTabActive, toggleMenu]);

  if (isMobile) {
    return (
      <Modal
        style={{ zIndex: zIndex.drawer }}
        componentsProps={{ backdrop: { onClick: onClickOverlay } }}
        open={isMenuActive}
      >
        <Slide
          in={isMenuActive}
          direction="right"
        >
          <BurgerMenuRoot
            ownerState={ownerState}
            data-testid={getTestId('burger-menu-root')}
          >
            <BurgerMenuContainer data-testid={getTestId('burger-menu-container')}>
              <BurgerMenuTab
                activeTab={activeTab}
                tabName={BurgerMenuTabNames.ProductsTab}
                tabIcon={BurgerMenuIcon as typeof SvgIcon}
                onTabClick={handleTabClick}
              >
                {translations(TranslationKeys.Products)}
              </BurgerMenuTab>
              <BurgerMenuTab
                activeTab={activeTab}
                tabName={BurgerMenuTabNames.AccountTab}
                tabIcon={
                  isAccountTabActive ? (UserFilled as typeof SvgIcon) : (User as typeof SvgIcon)
                }
                isLoggedIn={isLoggedIn}
                onTabClick={handleTabClick}
              >
                {isLoggedIn
                  ? translations(TranslationKeys.MyAccount)
                  : `${translations(TranslationKeys.LogIn)}/${translations(
                      TranslationKeys.Register,
                    )}`}
              </BurgerMenuTab>
            </BurgerMenuContainer>
            {activeTab === BurgerMenuTabNames.ProductsTab && (
              <LazyBurgerMenuLinks isBrandPartner={isBrandPartner} />
            )}
            {activeTab === BurgerMenuTabNames.AccountTab && accountTabContent}
          </BurgerMenuRoot>
        </Slide>
      </Modal>
    );
  }

  if (!isMenuActive || burgerMenuTab === BurgerMenuTabNames.AccountTab) {
    return null;
  }

  return (
    <BurgerMenuRoot
      as="div"
      ownerState={{ ...ownerState, isDesktop: true }}
      data-testid={getTestId('burger-menu-root')}
    >
      <BurgerMenuLinks isBrandPartner={isBrandPartner} />
      <BurgerMenuContent />
      <Backdrop
        open
        sx={{ position: 'absolute', zIndex: '-1', width: '100vw', height: '100vh' }}
        onClick={onClickOverlay}
      />
    </BurgerMenuRoot>
  );
});
